import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaMapMarkerAlt, FaCheck, FaTimes, FaWhatsapp } from 'react-icons/fa';
import { isServiceable, savePincode } from '../data/serviceablePincodes';
import { whatsappNumber } from '../data/productsData';
import { calculateShipping, saveShippingInfo } from '../utils/shippingUtils';
import { useEffect } from 'react';
import './PincodeChecker.css';

const PincodeChecker = () => {
    // Initialize with empty string, do not load saved pincode
    const [pincode, setPincode] = useState('');
    const [result, setResult] = useState(null); // { valid, district, message, shipping }
    const [isChecking, setIsChecking] = useState(false);

    // Removed auto-check useEffect to ensure user manually enters data

    const performCheck = (pin) => {
        setIsChecking(true);
        // Simulate brief loading for better UX
        setTimeout(() => {
            const checkResult = isServiceable(pin);

            // Calculate shipping if valid
            let shippingInfo = null;
            if (checkResult.valid) {
                const shipping = calculateShipping(pin);
                shippingInfo = shipping;

                // Save pincode and shipping info
                savePincode(pin);
                if (!shipping.error) {
                    saveShippingInfo(pin, shipping.amount, shipping.location);
                }
            }

            setResult({
                ...checkResult,
                shipping: shippingInfo
            });
            setIsChecking(false);
        }, 400);
    }

    const handleInputChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setPincode(value);
        // Clear result ONLY if the user clears the input or changes it significantly
        // But for better UX, let's clear persistence if they type?
        // Actually, let's keep the result visible but maybe grayed out?
        // For now, standard behavior: clear result if input changes
        if (result) setResult(null);
    };

    const handleCheck = () => {
        if (pincode.length !== 6) {
            setResult({ valid: false, message: 'Please enter a valid 6-digit pincode' });
            return;
        }
        performCheck(pincode);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleCheck();
        }
    };

    const whatsappLink = `https://wa.me/${whatsappNumber}?text=Hi! I'd like to inquire about delivery to pincode ${pincode}`;

    return (
        <motion.div
            className="pincode-checker"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
        >
            <div className="pincode-checker-header">
                <FaMapMarkerAlt />
                <span>Check Delivery Availability</span>
            </div>

            <div className="pincode-input-row">
                <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="pincode-input"
                    placeholder="Enter 6-digit pincode"
                    value={pincode}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    maxLength={6}
                    aria-label="Enter pincode"
                />
                <motion.button
                    className="pincode-check-btn"
                    onClick={handleCheck}
                    disabled={isChecking || pincode.length < 6}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                >
                    {isChecking ? (
                        <div className="pincode-spinner" />
                    ) : (
                        'Check'
                    )}
                </motion.button>
            </div>

            <AnimatePresence mode="wait">
                {result && (
                    <motion.div
                        className={`pincode-result ${result.valid ? 'success' : 'error'}`}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                    >
                        <span className="pincode-result-icon">
                            {result.valid ? <FaCheck /> : <FaTimes />}
                        </span>
                        <div className="pincode-result-content">
                            <p className="pincode-result-title">
                                {result.valid
                                    ? `Delivery available to this Location`
                                    : result.message
                                }
                            </p>
                            <p className="pincode-result-subtitle">
                                {result.valid
                                    ? 'Usually delivered in 3-7 business days'
                                    : 'Please check the pincode and try again.'
                                }
                            </p>
                            {result.valid && result.shipping && !result.shipping.error && (
                                <p className="pincode-shipping-cost">
                                    📦 Shipping: ₹{result.shipping.amount}
                                </p>
                            )}
                            {!result.valid && pincode.length === 6 && (
                                <a
                                    href={whatsappLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="pincode-contact-link"
                                >
                                    <FaWhatsapp />
                                    Contact for special arrangements
                                </a>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default PincodeChecker;
